import enTranslations from '../translations/en.json';
import { NotificationType } from '../../types/notification';
import type { Notification } from '../../types/notification';
import { formatDateTime } from './formatUtils';
import { getUserTimezone } from './locationUtils';

type UserRole = 'traveller' | 'sender';

/**
 * Interpolate {{key}} placeholders in a translation string
 */
const interpolate = (template: string, params: Record<string, string | number>): string => {
  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), String(value));
  }, template);
};

/**
 * Extract the person's name from the backend message string.
 * Backend messages typically start with "Name has sent/accepted/rejected..."
 */
const extractNameFromMessage = (message: string): string => {
  if (!message) return '';
  // Match name at the start of message before common verb phrases
  const match = message.match(/^(.+?)\s+(?:has\s+)?(sent|accepted|rejected|declined|completed)/i);
  return match?.[1] || '';
};

/**
 * Format a raw backend drop-off date/time into the same human-readable,
 * UTC-formatted style used on the itinerary details page (e.g. "Jul 9, 2026" / "3:40 PM").
 */
const formatDropOff = (dateValue: unknown, timeValue: unknown): { date: string; time: string } => {
  const dateString = String(dateValue || '');
  const timeString = String(timeValue || '');
  if (!dateString) return { date: '', time: '' };

  const formatted = formatDateTime(dateString, timeString || undefined, undefined, undefined, getUserTimezone());
  if (formatted === 'N/A') return { date: dateString, time: timeString };

  // formatDateTime yields "Jun 23, 2026, 3:40 PM" when a time is included
  const parts = formatted.split(',').map((part) => part.trim());
  if (!timeString || parts.length < 3) {
    return { date: parts.join(', '), time: '' };
  }
  return { date: `${parts[0]}, ${parts[1]}`, time: parts[2] };
};

/**
 * Extract display params from notification data for interpolation
 */
const extractParams = (notification: Notification): Record<string, string | number> => {
  const data = (notification.data || {}) as Record<string, unknown>;

  const categoryNames = Array.isArray(data.category_names)
    ? data.category_names.join(', ')
    : data.category_names || '';

  // Name: try dedicated fields first, then extract from backend message
  const rawName = data.initiatorName || data.sender_name || data.traveler_name || data.name
    || extractNameFromMessage(notification.message)
    || '';
  const name = String(rawName).replace(/^"|"$/g, '');

  const itineraryName = String(data.itinerary_name || data.itineraryName || '');
  const shipmentName = String(data.shipment_name || data.shipmentName || '');
  const dropOff = formatDropOff(data.drop_of_date, data.drop_of_time);

  return {
    name,
    shipmentName,
    itineraryName,
    itineraryNamePart: itineraryName ? ` on itinerary **${itineraryName}**` : '',
    shipmentNamePart: shipmentName ? ` on item **${shipmentName}**` : '',
    orderCode: String(data.order_code || ''),
    categoryNames: String(categoryNames),
    categoryName: String(categoryNames),
    price: String(data.price || ''),
    pricePerLb: String(data.price_per_lb || ''),
    reason: String(data.rejection_reason || data.withdrawal_reason || data.reason || ''),
    dropOfDate: dropOff.date,
    dropOfTime: dropOff.time,
    dropOffAddress: String(data.sender_drop_off_address || ''),
    flightNumber: String(data.flight_number || ''),
    expiresAtLocal: String(data.expired_at_local || ''),
    chatRoomId: String(data.chat_roomId ?? ''),
  };
};

/**
 * Get the translation entry for a notification type + user role
 */
const getTranslationEntry = (
  notification: Notification,
  role: UserRole
): { title?: string; message?: string } | null => {
  const roleKey = role === 'traveller' ? 'traveler' : 'sender';
  const roleTranslations = (enTranslations.offerNotifications as any)?.[roleKey];
  if (!roleTranslations) return null;

  // offer_expired's message depends on whose offer expired (data.type),
  // not just who is receiving it, so it uses a composite translation key.
  let type: string = notification.type;
  if (notification.type === NotificationType.OFFER_EXPIRED) {
    const offerOwner = notification.data?.type === 'sender' ? 'sender' : 'traveler';
    type = `offer_expired_${offerOwner}`;
  }

  // chat room created translations need the order code; without it, keep the backend's own text
  if (
    (notification.type === NotificationType.CHAT_ROOM_CREATED ||
      notification.type === NotificationType.GROUP_CHAT_ROOM_CREATED) &&
    !notification.data?.order_code
  ) {
    return null;
  }

  if (!roleTranslations[type]) return null;
  return roleTranslations[type];
};

/**
 * Get translated title for a notification based on its type and user role.
 * Falls back to the backend-provided title if no translation exists.
 */
export const getNotificationTitle = (
  notification: Notification,
  role?: UserRole
): string => {
  const userRole = role || notification.user_role;
  if (!userRole) return notification.title;

  const entry = getTranslationEntry(notification, userRole);
  if (!entry?.title) return notification.title;

  const params = extractParams(notification);
  return interpolate(entry.title, params);
};

/**
 * Get translated message for a notification based on its type and user role.
 * Falls back to the backend-provided message if no translation exists.
 */
export const getNotificationMessage = (
  notification: Notification,
  role?: UserRole
): string => {
  const userRole = role || notification.user_role;
  if (!userRole) return notification.message;

  const entry = getTranslationEntry(notification, userRole);
  if (!entry?.message) return notification.message;

  const params = extractParams(notification);
  return interpolate(entry.message, params);
};

/**
 * Get the pending offer message for pulsating icon based on user role
 */
export const getPendingOfferMessage = (role: UserRole): string => {
  const roleKey = role === 'traveller' ? 'traveler' : 'sender';
  const msg = (enTranslations.offerNotifications as any)?.[roleKey]?.pendingOffer?.message;
  return msg || 'You have a pending shipment offer request. Please accept or reject the offer.';
};
